import React, { useCallback, useEffect, useState } from 'react';
import { Switch } from '@mantine/core';
import { Button } from '../../../../components/Button';
import type { PrinterAdminView } from '../../../../services/printers/printers.service';
import { printersService } from '../../../../services/printers/printers.service';
import classes from './shared/settings-shared.module.scss';

type PrinterManagementSectionProps = {
  tenantId: string;
};

// Summary: Super-admin table of all printers; billable + disable toggles PATCH diff-only.
export const PrinterManagementSection: React.FC<PrinterManagementSectionProps> = ({ tenantId }) => {
  const [printers, setPrinters] = useState<PrinterAdminView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setPrinters(await printersService.listManagedPrinters(tenantId));
    } catch (err: unknown) {
      setError(err instanceof Error && err.message ? err.message : 'טעינת המדפסות נכשלה');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchConfig = useCallback(
    async (printerId: number, patch: { billable?: boolean; humanDisabled?: boolean }) => {
      setBusyId(printerId);
      setError(null);
      try {
        await printersService.updatePrinterConfig(tenantId, printerId, patch);
        await load();
      } catch (err: any) {
        setError(err?.message || 'עדכון המדפסת נכשל');
      } finally {
        setBusyId(null);
      }
    },
    [tenantId, load],
  );

  return (
    <section data-testid="printer-management-section" className={classes.section}>
      <div>
        <h4 className={classes.sectionHeading}>ניהול מדפסות</h4>
        <p className={classes.sectionSubheading}>
          בחר אילו מדפסות ידפיסו חשבונות וקבלות (מדפסת חשבונות), והשבת מדפסות ללא הדפסה. הבחירה נשמרת גם
          לאחר סנכרון מחדש של סוכן המדפסות.
        </p>
      </div>

      <div className={classes.formActions}>
        <Button
          type="button"
          dataTestId="printer-mgmt-refresh"
          variant="save"
          size="sm"
          onClick={() => void load()}
          loading={isLoading}
          loadingLabel="מרענן..."
        >
          רענן
        </Button>
      </div>

      {error && <div className={classes.error}>{error}</div>}

      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>שם</th>
              <th>סטטוס</th>
              <th>מגירת מזומן</th>
              <th className={classes.checkboxCell}>מדפסת חשבונות</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {printers.map((printer) => {
              const disabled = printer.humanDisabled;
              const busy = busyId === printer.id;
              return (
                <tr key={printer.id} data-testid={`printer-mgmt-row-${printer.id}`}>
                  <td>{printer.name}</td>
                  <td>
                    <span className={printer.active && !disabled ? classes.statusActive : classes.statusInactive}>
                      {disabled ? 'מושבת' : printer.active ? 'פעיל' : 'לא מחובר'}
                    </span>
                  </td>
                  <td>{printer.cashDrawer ? 'כן' : '—'}</td>
                  <td className={classes.checkboxCell}>
                    <Switch
                      data-testid={`printer-billable-toggle-${printer.id}`}
                      aria-label={`מדפסת חשבונות ${printer.name}`}
                      checked={printer.billable}
                      disabled={busy}
                      onChange={(event) => void patchConfig(printer.id, { billable: event.currentTarget.checked })}
                    />
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant={disabled ? 'confirm' : 'cancel'}
                      dataTestId={`printer-disable-toggle-${printer.id}`}
                      onClick={() => void patchConfig(printer.id, { humanDisabled: !disabled })}
                      disabled={busy}
                    >
                      {disabled ? 'הפעל' : 'השבת'}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {printers.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className={classes.empty}>
                  אין מדפסות רשומות עדיין. חבר סוכן מדפסות כדי לרשום מדפסות מקומיות.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
